export const BULK_UPDATE_MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;
export const BULK_UPDATE_MAX_ROWS = 500;
export const BULK_UPDATE_REQUIRED_COLUMNS = ['Batch Number', 'VIN', 'Value', 'Entity'] as const;
export const BULK_UPDATE_ACCEPTED_EXTENSION = '.xlsx';
export const BULK_UPDATE_ACCEPTED_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
export const BULK_UPDATE_VALUE_REQUIRED_NOTE =
  'Batch Number, Value and Entity are mandatory. Value can never be left blank or set to null.';

const BATCH_NUMBER_LENGTH = 10;
const BATCH_NUMBER_PATTERN = /^\d+$/;
const BATCH_NUMBER_PREFIX_PATTERN = /^(000|9100)/;
// Excludes I, O and Q, which are not valid VIN characters.
const VIN_PATTERN = /^[A-HJ-NPR-Z0-9]{17}$/;
const COUNTRY_CODE_PATTERN = /^[A-Z]{2}$/;
const TOGGLE_VALUES = ['Enabled', 'Disabled'];

export type TBulkUpdateEntity = 'Studio Select Discount' | 'Agent Listing Country' | 'Importer Listing Country';

export interface ICountryOption {
  label: string;
  value: string;
}

// Country codes accepted in the bulk update file, per RTLCOMMSYS-47280.
const AGENT_LISTING_COUNTRY_OPTIONS: ICountryOption[] = [
  { label: 'Germany', value: 'DE' },
  { label: 'Switzerland', value: 'CH' },
  { label: 'Netherlands', value: 'NL' },
  { label: 'Norway', value: 'NO' },
];

const IMPORTER_LISTING_COUNTRY_OPTIONS: ICountryOption[] = [{ label: 'Belgium', value: 'BE' }];

export interface IBulkUpdateEntityRule {
  entity: TBulkUpdateEntity;
  description: string;
  valueHint: string;
  sampleValues: string[];
  allowedCountries?: ICountryOption[];
  allowedValues?: string[];
  validateValue: (value: string) => string | null;
  normalizeValue: (value: string) => string;
}

const splitCountryCodes = (value: string): string[] => value.split(',').map((code) => code.trim().toUpperCase());

const validateToggleValue = (value: string): string | null => {
  const isValid = TOGGLE_VALUES.some((allowed) => allowed.toLowerCase() === value.toLowerCase());
  return isValid ? null : `Value must be one of: ${TOGGLE_VALUES.join(', ')}.`;
};

const normalizeToggleValue = (value: string): string =>
  TOGGLE_VALUES.find((allowed) => allowed.toLowerCase() === value.toLowerCase()) ?? value;

const createCountryListValidator =
  (allowedCountries: ICountryOption[]) =>
  (value: string): string | null => {
    const codes = splitCountryCodes(value);
    if (codes.some((code) => code === '')) {
      return 'Value contains an empty country code.';
    }
    const malformedCodes = codes.filter((code) => !COUNTRY_CODE_PATTERN.test(code));
    if (malformedCodes.length > 0) {
      return `Invalid country code(s): ${malformedCodes.join(', ')}. Use 2-letter codes separated by commas.`;
    }
    const allowedCodes = allowedCountries.map((country) => country.value);
    const unsupportedCodes = codes.filter((code) => !allowedCodes.includes(code));
    if (unsupportedCodes.length > 0) {
      return `Unsupported country code(s): ${unsupportedCodes.join(', ')}. Allowed codes: ${allowedCodes.join(', ')}.`;
    }
    if (new Set(codes).size !== codes.length) {
      return 'Value contains duplicate country codes.';
    }
    return null;
  };

const normalizeCountryListValue = (value: string): string => splitCountryCodes(value).join(',');

export const BULK_UPDATE_ENTITY_RULES: IBulkUpdateEntityRule[] = [
  {
    entity: 'Studio Select Discount',
    description: 'Enable or disable the Studio Select discount for each vehicle.',
    valueHint: 'Value must be exactly Enabled or Disabled.',
    sampleValues: ['Enabled', 'Disabled'],
    allowedValues: TOGGLE_VALUES,
    validateValue: validateToggleValue,
    normalizeValue: normalizeToggleValue,
  },
  {
    entity: 'Agent Listing Country',
    description: 'Assign the agent listing countries for each vehicle.',
    valueHint: 'Value must use the 2-letter country codes below, separated by commas (for example DE,NL).',
    sampleValues: ['DE,NL'],
    allowedCountries: AGENT_LISTING_COUNTRY_OPTIONS,
    validateValue: createCountryListValidator(AGENT_LISTING_COUNTRY_OPTIONS),
    normalizeValue: normalizeCountryListValue,
  },
  {
    entity: 'Importer Listing Country',
    description: 'Assign the importer listing countries for each vehicle.',
    valueHint: 'Value must use the 2-letter country codes below, separated by commas (for example BE).',
    sampleValues: ['BE'],
    allowedCountries: IMPORTER_LISTING_COUNTRY_OPTIONS,
    validateValue: createCountryListValidator(IMPORTER_LISTING_COUNTRY_OPTIONS),
    normalizeValue: normalizeCountryListValue,
  },
];

const ENTITY_RULES_BY_KEY = new Map<string, IBulkUpdateEntityRule>(
  BULK_UPDATE_ENTITY_RULES.map((rule) => [rule.entity.toLowerCase(), rule])
);

export interface IBulkUpdateRow {
  batchNumber: string;
  vin: string;
  entity: string;
  value: string;
}

export interface IBulkUpdateRowError {
  row: number;
  column: string;
  message: string;
}

export interface IBulkUpdateValidationResult {
  fatalError: string | null;
  rows: IBulkUpdateRow[];
  errors: IBulkUpdateRowError[];
}

export const formatFileSize = (bytes: number): string => `${(bytes / (1024 * 1024)).toFixed(2)} MB`;

export const validateBulkUpdateFile = (file: File): string | null => {
  if (!file.name.toLowerCase().endsWith(BULK_UPDATE_ACCEPTED_EXTENSION)) {
    return 'Unsupported file type. Please upload an .xlsx file.';
  }
  if (file.type && file.type !== BULK_UPDATE_ACCEPTED_MIME_TYPE) {
    return 'Unsupported file type. Please upload an .xlsx file.';
  }
  if (file.size === 0) {
    return 'The selected file is empty.';
  }
  if (file.size > BULK_UPDATE_MAX_FILE_SIZE_BYTES) {
    return `File size ${formatFileSize(file.size)} exceeds the ${formatFileSize(
      BULK_UPDATE_MAX_FILE_SIZE_BYTES
    )} limit.`;
  }
  return null;
};

const toCellText = (cell: unknown): string => {
  if (cell === null || cell === undefined) {
    return '';
  }
  if (cell instanceof Date) {
    return cell.toISOString();
  }
  return String(cell).trim();
};

const normalizeHeader = (cell: unknown): string => toCellText(cell).toLowerCase().replace(/\s+/g, ' ');

export const findMissingColumns = (headerRow: unknown[]): string[] => {
  const headers = headerRow.map(normalizeHeader);
  return BULK_UPDATE_REQUIRED_COLUMNS.filter((column) => !headers.includes(normalizeHeader(column)));
};

const buildColumnIndexes = (headerRow: unknown[]): Record<string, number> => {
  const headers = headerRow.map(normalizeHeader);
  return BULK_UPDATE_REQUIRED_COLUMNS.reduce<Record<string, number>>((acc, column) => {
    acc[column] = headers.indexOf(normalizeHeader(column));
    return acc;
  }, {});
};

const isBlankRow = (row: unknown[]): boolean => !row?.some((cell) => toCellText(cell) !== '');

const readBatchNumber = (cell: unknown): string => {
  const text = toCellText(cell);
  // Excel strips leading zeros when a batch number is stored as a number, so restore the fixed width.
  return BATCH_NUMBER_PATTERN.test(text) ? text.padStart(BATCH_NUMBER_LENGTH, '0') : text;
};

const readRow = (raw: unknown[], indexes: Record<string, number>): IBulkUpdateRow => ({
  batchNumber: readBatchNumber(raw[indexes['Batch Number']]),
  vin: toCellText(raw[indexes.VIN]).toUpperCase(),
  entity: toCellText(raw[indexes.Entity]),
  value: toCellText(raw[indexes.Value]),
});

const validateBatchNumber = (batchNumber: string): string | null => {
  if (!batchNumber) {
    return 'Batch Number is required.';
  }
  if (!BATCH_NUMBER_PATTERN.test(batchNumber)) {
    return 'Batch Number must contain digits only.';
  }
  return BATCH_NUMBER_PREFIX_PATTERN.test(batchNumber) ? null : 'Batch Number must start with 000 or 9100.';
};

const validateVin = (vin: string, rawCell: unknown): string | null => {
  if (!vin) {
    return null;
  }
  // A 17-digit VIN exceeds the safe integer range, so Excel has already lost digits by this point.
  if (typeof rawCell === 'number') {
    return 'VIN must be stored as text. Format the VIN column as Text in Excel and re-enter the value.';
  }
  return VIN_PATTERN.test(vin) ? null : 'VIN must be 17 characters and must not contain I, O or Q.';
};

const validateEntity = (entity: string): string | null => {
  if (!entity) {
    return 'Entity is required.';
  }
  return ENTITY_RULES_BY_KEY.has(entity.toLowerCase())
    ? null
    : `Unknown entity. Supported entities: ${BULK_UPDATE_ENTITY_RULES.map((rule) => rule.entity).join(', ')}.`;
};

const validateValue = (entity: string, value: string): string | null => {
  if (!value) {
    return 'Value is required and cannot be empty or null.';
  }
  const rule = ENTITY_RULES_BY_KEY.get(entity.toLowerCase());
  return rule ? rule.validateValue(value) : null;
};

const validateRow = (row: IBulkUpdateRow, rowNumber: number, rawVin: unknown): IBulkUpdateRowError[] => {
  const checks: Array<[string, string | null]> = [
    ['Batch Number', validateBatchNumber(row.batchNumber)],
    ['VIN', validateVin(row.vin, rawVin)],
    ['Entity', validateEntity(row.entity)],
    ['Value', validateValue(row.entity, row.value)],
  ];
  return checks
    .filter(([, message]) => message !== null)
    .map(([column, message]) => ({ row: rowNumber, column, message: message as string }));
};

const normalizeRow = (row: IBulkUpdateRow): IBulkUpdateRow => {
  const rule = ENTITY_RULES_BY_KEY.get(row.entity.toLowerCase());
  if (!rule) {
    return row;
  }
  return { ...row, entity: rule.entity, value: rule.normalizeValue(row.value) };
};

const emptyResult = (fatalError: string): IBulkUpdateValidationResult => ({ fatalError, rows: [], errors: [] });

export const validateBulkUpdateSheet = (sheet: unknown[][]): IBulkUpdateValidationResult => {
  if (!sheet || sheet.length === 0) {
    return emptyResult('The file is empty.');
  }

  const missingColumns = findMissingColumns(sheet[0] ?? []);
  if (missingColumns.length > 0) {
    return emptyResult(`Missing required column(s): ${missingColumns.join(', ')}.`);
  }

  const dataRows = sheet
    .slice(1)
    .map((raw, index) => ({ raw, rowNumber: index + 2 }))
    .filter((entry) => !isBlankRow(entry.raw));

  if (dataRows.length === 0) {
    return emptyResult('The file does not contain any data rows.');
  }
  if (dataRows.length > BULK_UPDATE_MAX_ROWS) {
    return emptyResult(`The file contains ${dataRows.length} rows. A maximum of ${BULK_UPDATE_MAX_ROWS} is allowed.`);
  }

  const indexes = buildColumnIndexes(sheet[0]);
  const errors: IBulkUpdateRowError[] = [];
  const rows: IBulkUpdateRow[] = [];
  const seenKeys = new Set<string>();

  dataRows.forEach(({ raw, rowNumber }) => {
    const row = readRow(raw, indexes);
    const rowErrors = validateRow(row, rowNumber, raw[indexes.VIN]);
    if (rowErrors.length > 0) {
      errors.push(...rowErrors);
      return;
    }

    const normalized = normalizeRow(row);
    const key = `${normalized.batchNumber}|${normalized.entity}`;
    if (seenKeys.has(key)) {
      errors.push({
        row: rowNumber,
        column: 'Batch Number',
        message: `Duplicate entry for Batch Number ${normalized.batchNumber} and entity ${normalized.entity}.`,
      });
      return;
    }

    seenKeys.add(key);
    rows.push(normalized);
  });

  return { fatalError: null, rows, errors };
};
